/**
 * ย่อรูปสลิปในเบราว์เซอร์ก่อนส่งขึ้น Cloud Function
 *
 * รูปที่ถ่ายจากมือถือรุ่นใหม่อยู่ที่ 3-8 MB ซึ่งเกินเพดาน 4 MB ที่ฝั่ง function ตั้งไว้
 * และเป็นขั้นที่ช้าที่สุดของการสแกนเมื่อใช้เน็ตมือถือ — ย่อก่อนจึงช่วยทั้งสองเรื่อง
 *
 * ## จำกัดด้วยจำนวนพิกเซลรวม ไม่ใช่ด้านยาว
 *
 * สลิปจากแอปโบรกมักเป็น screenshot ยาวทั้งหน้า (เช่น Dime กว้าง 1080 สูงเกือบ 4000)
 * เดิมจำกัดด้านยาวไว้ 1600px รูปแบบนี้จะถูกบีบจนกว้างเหลือราว 400px ตัวหนังสือเล็กจน
 * OCR อ่านหลักทศนิยมพลาด — จำกัดที่พิกเซลรวมทำให้รูปยาวยังคงความกว้างไว้ได้
 * ส่วนรูปถ่ายปกติยังถูกย่อลงเหมือนเดิม และ JPEG ขนาดนี้ยังต่ำกว่าเพดาน 4 MB สบาย ๆ
 */

/** จำนวนพิกเซลรวมสูงสุดหลังย่อ (~4 ล้าน เท่ากับ 1080 × 3700) */
const MAX_PIXELS = 4_000_000;

/** ด้านที่ยาวที่สุดหลังย่อ — กันรูปที่ยาวผิดปกติจนความกว้างเหลือไม่กี่พิกเซล */
const MAX_EDGE_PX = 4096;

/** คุณภาพ JPEG — ต่ำกว่านี้ตัวเลขบนสลิปเริ่มมี artifact จนอ่านผิดหลัก */
const JPEG_QUALITY = 0.85;

export interface EncodedImage {
  /** base64 ล้วน ไม่มีส่วนหัว data URL */
  base64: string;
  mimeType: string;
}

/**
 * ย่อแล้วแปลงเป็น base64 พร้อมส่ง
 *
 * ถ้าย่อไม่ได้ด้วยเหตุใดก็ตาม (codec ที่เบราว์เซอร์ไม่รู้จัก, หน่วยความจำไม่พอ)
 * จะส่งไฟล์เดิมไปแทน แล้วปล่อยให้ด่านขนาดกับชนิดไฟล์ฝั่ง function เป็นคนตัดสิน —
 * ดีกว่าปฏิเสธเองตรงนี้ทั้งที่ไฟล์อาจเล็กพออยู่แล้ว
 */
export async function encodeImageForUpload(file: File): Promise<EncodedImage> {
  const resized = await downscale(file);
  return { base64: await toBase64(resized), mimeType: resized.type };
}

async function downscale(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(
      1,
      Math.sqrt(MAX_PIXELS / (bitmap.width * bitmap.height)),
      MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height),
    );

    if (scale === 1) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext('2d');
    if (context === null) {
      bitmap.close();
      return file;
    }

    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
    });

    return blob ?? file;
  } catch {
    return file;
  }
}

/** `FileReader` คืน data URL — ตัดส่วนหัวออกให้เหลือ base64 ล้วนตามที่ function รับ */
function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('อ่านไฟล์รูปไม่สำเร็จ'));
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^;]+;base64,/, ''));
    reader.readAsDataURL(blob);
  });
}
